/**
 * Client helpers for BPM approval rule drafts (local Mongo — no Oracle writes).
 */

export function formatDraftApiError(err, fallback) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const serverMsg = data?.error ? String(data.error) : '';
  if (status === 404) {
    if (/draft not found/i.test(serverMsg)) {
      return 'Draft not found — click Save draft first, or refresh the rules list.';
    }
    return (
      'Draft API not found (404). Restart the API server so /api/copilot/bpm/drafts is registered.'
    );
  }
  if (status === 503 && /mongo/i.test(serverMsg)) {
    return serverMsg || 'MongoDB is not configured — set MONGODB_URI on the server.';
  }
  return serverMsg || err?.message || fallback;
}

function normalizeRuleNameKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Keep one row per ruleId and per display name (newest updatedAt wins). */
export function dedupeApprovalRules(rules) {
  if (!Array.isArray(rules) || rules.length < 2) return rules || [];

  const sorted = [...rules].sort((a, b) => {
    const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return tb - ta;
  });

  const seenIds = new Set();
  const seenNames = new Set();
  const out = [];

  for (const rule of sorted) {
    const id = rule?.id ? String(rule.id) : '';
    const nameKey = normalizeRuleNameKey(rule?.name);
    if (id && seenIds.has(id)) continue;
    if (nameKey && seenNames.has(nameKey)) continue;
    if (id) seenIds.add(id);
    if (nameKey) seenNames.add(nameKey);
    out.push(rule);
  }

  return out;
}

export function mapDraftToRule(draft) {
  if (!draft) return null;
  return {
    id: draft.ruleId,
    mongoId: draft.id,
    name: draft.ruleName,
    transaction: draft.transaction || draft.design?.transactionHint || '',
    status: draft.status === 'PUBLISHED' ? 'published' : 'draft',
    draftStatus: draft.status,
    summary: draft.summary || draft.design?.summary || '',
    design: draft.design,
    transactionCategory: draft.transactionCategory || 'absence',
    updatedAt: draft.updatedAt,
  };
}

export async function listApprovalDrafts(api) {
  const res = await api.get('/api/copilot/bpm/drafts');
  return dedupeApprovalRules((res.data?.drafts || []).map(mapDraftToRule));
}

export async function saveApprovalDraft(api, payload) {
  const res = await api.post('/api/copilot/bpm/drafts', payload);
  return mapDraftToRule(res.data?.draft);
}

export async function updateApprovalDraft(api, mongoId, payload) {
  const res = await api.put(`/api/copilot/bpm/drafts/${mongoId}`, payload);
  return mapDraftToRule(res.data?.draft);
}

export async function compareApprovalDraft(api, mongoId) {
  const res = await api.post(`/api/copilot/bpm/drafts/${mongoId}/compare`);
  return res.data;
}

export async function simulateApprovalDraft(api, mongoId, { category, personNumber } = {}) {
  const res = await api.post(`/api/copilot/bpm/drafts/${mongoId}/simulate`, {
    category,
    personNumber,
  });
  return res.data;
}

export async function deployApprovalDraft(api, mongoId, { connectionId, category, dryRun = false }) {
  const res = await api.post(
    `/api/copilot/bpm/drafts/${mongoId}/deploy`,
    { confirm: true, connectionId, category, dryRun },
    { timeout: 120000 }
  );
  return res.data;
}
